/**
 * <plw-product-card> — the universal PLW product card (CQ-1958).
 *
 * Progressive enhancement only. The default/alternate image crossfade and the
 * hover-reveal of the swatch picker are pure CSS (assets/base.css); this
 * component handles the one interactive bit: choosing a colour swatch swaps the
 * main image to that colour's variant image and repoints the card link.
 *
 * Markup contract (see snippets/plw-product-card.liquid):
 *   [data-plw-primary]      the main <img> to swap
 *   [data-plw-secondary]    the hover <img> (the colour's flat shot, swapped on select)
 *   [data-plw-media-link]   the image's <a> (href repointed on select)
 *   [data-plw-info-link]    the name/price <a> (href repointed on select)
 *   input[type=radio][data-src][data-srcset][data-alt][data-url]  one per colour
 *     (+ [data-flat-src][data-flat-srcset] when the colour has a flat shot)
 */
class PlwProductCard extends HTMLElement {
  connectedCallback() {
    this.primary = this.querySelector('[data-plw-primary]');
    this.secondary = this.querySelector('[data-plw-secondary]');
    this.links = this.querySelectorAll('[data-plw-media-link], [data-plw-info-link]');
    this.inputs = this.querySelectorAll('input[type="radio"][data-url]');
    this.status = this.querySelector('[data-plw-swatch-status]');
    // Only flat-driven cards manage the hover image — legacy products without
    // flats keep their static second-image crossfade untouched.
    this.hasFlats = Array.from(this.inputs).some((input) => input.dataset.flatSrc);

    this.handleChange = this.handleChange.bind(this);
    for (const input of this.inputs) input.addEventListener('change', this.handleChange);

    // A facet change re-renders the section server-side and morphs it in
    // (facets.js → sectionRenderer → morph.js): the newly resting colour
    // arrives as the `checked` ATTRIBUTE, alongside the already-swapped image
    // and links. A radio the shopper has clicked keeps its dirty checkedness
    // and ignores attribute changes, so mirror the attribute onto the property.
    this.checkedObserver = new MutationObserver(this.syncChecked.bind(this));
    for (const input of this.inputs) {
      this.checkedObserver.observe(input, { attributes: true, attributeFilter: ['checked'] });
    }
  }

  disconnectedCallback() {
    for (const input of this.inputs) input.removeEventListener('change', this.handleChange);
    this.checkedObserver?.disconnect();
  }

  syncChecked() {
    for (const input of this.inputs) input.checked = input.hasAttribute('checked');
  }

  handleChange(event) {
    const input = event.currentTarget;

    if (this.primary && input.dataset.src) {
      this.primary.src = input.dataset.src;
      this.primary.srcset = input.dataset.srcset || '';
      if (input.dataset.alt != null) this.primary.alt = input.dataset.alt;
    }

    if (this.secondary && this.hasFlats) {
      if (input.dataset.flatSrc) {
        this.secondary.src = input.dataset.flatSrc;
        this.secondary.srcset = input.dataset.flatSrcset || '';
        this.secondary.hidden = false;
      } else {
        // No flat for this colour — hide the hover image rather than crossfade
        // to another colour's flat.
        this.secondary.hidden = true;
      }
    }

    if (input.dataset.url) {
      for (const link of this.links) link.href = input.dataset.url;
    }

    // Announce the swap (the card image is decorative/aria-hidden, so the alt
    // change alone is silent for AT). The input value is the colour name.
    if (this.status) this.status.textContent = input.value;
  }
}

if (!customElements.get('plw-product-card')) {
  customElements.define('plw-product-card', PlwProductCard);
}
